import { Badge, Loader } from '@mantine/core';
import { IconCalendar, IconClipboardCheck } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import useFetchMyTasks from 'src/Hooks/Queries/useFetchMyTasks';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { TaskStatus } from 'src/types/task.types';

import styles from './TasksPopup.module.scss';
import { Props } from './TasksPopup.types';

const getStatusBadgeColor = (status: TaskStatus) => {
  switch (status) {
    case TaskStatus.TODO:
      return 'orange';
    case TaskStatus.IN_PROGRESS:
      return 'blue';
    default:
      return 'gray';
  }
};

const getStatusLabel = (status: TaskStatus, t: (key: string, fallback: string) => string) => {
  switch (status) {
    case TaskStatus.TODO:
      return t('tasks.status.todo', 'À faire');
    case TaskStatus.IN_PROGRESS:
      return t('tasks.status.inProgress', 'En cours');
    default:
      return '';
  }
};

const TasksPopup = ({ onClose }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentAssetId = useCurrentAssetId();
  const { data: tasks, isLoading } = useFetchMyTasks();

  const overdueTasks = useMemo(() => {
    if (!tasks) return [];
    const startOfDay = dayjs().startOf('day');
    return tasks.filter(task => task.dueDate && dayjs(task.dueDate).isBefore(startOfDay));
  }, [tasks]);

  const handleTaskClick = (assetId: string, taskId: string) => {
    navigate(`/asset/${assetId}/tasks/${taskId}`);
    onClose();
  };

  const handleSeeAllClick = () => {
    const targetAssetId = currentAssetId ?? overdueTasks[0]?.assetId;
    if (targetAssetId) navigate(`/asset/${targetAssetId}/tasks`);
    onClose();
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <Loader size="sm" />
        </div>
      </div>
    );
  }

  if (overdueTasks.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <IconClipboardCheck size={32} />
          <span className={styles.emptyText}>
            {t('tasks.popup.emptyOverdue', 'Aucune tâche en retard')}
          </span>
        </div>
        <div className={styles.footer} onClick={handleSeeAllClick}>
          {t('tasks.popup.seeAll', 'Voir toutes mes tâches')}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.taskList}>
        {overdueTasks.map(task => (
          <div
            key={task.id}
            className={styles.taskItem}
            onClick={() => handleTaskClick(task.assetId, task.id)}
          >
            <div className={styles.taskInfo}>
              <div className={styles.taskTitle}>{task.title}</div>
              <div className={styles.taskAssetName}>{task.assetName}</div>
            </div>
            <div className={styles.taskMeta}>
              {task.dueDate && (
                <div className={`${styles.dueDate} ${styles.overdue}`}>
                  <IconCalendar size={14} />
                  <span>{dayjs(task.dueDate).format('DD/MM')}</span>
                </div>
              )}
              <Badge size="sm" color={getStatusBadgeColor(task.status)}>
                {getStatusLabel(task.status, t)}
              </Badge>
            </div>
          </div>
        ))}
      </div>
      <div className={styles.footer} onClick={handleSeeAllClick}>
        {t('tasks.popup.seeAll', 'Voir toutes mes tâches')}
      </div>
    </div>
  );
};

export default TasksPopup;
